// 2FA stepup challenge — single global handler.
//
// When any axios call comes back with 401 {detail: '2fa_stepup_required'},
// we open a modal asking the user for their 6-digit TOTP (or backup code),
// exchange it for a 5-min stepup token, then retry the original request
// with `X-2FA-Token: <stepup>` attached. On 403 {detail: 'enroll_2fa_required'},
// we redirect the user to /dashboard?tab=security.
//
// Why a global event bus instead of prop-drilling: this can happen from
// ANY page that hits an admin endpoint. We don't want every page to wire
// its own modal.

const CHALLENGE_EVENT = "globaleyes:twofa-challenge";
const STEPUP_KEY = "ge:stepupToken";
const STEPUP_EXP_KEY = "ge:stepupTokenExp";

export function getStoredStepupToken() {
  try {
    const exp = parseInt(window.localStorage.getItem(STEPUP_EXP_KEY) || "0", 10);
    if (!exp || Date.now() > exp) {
      window.localStorage.removeItem(STEPUP_KEY);
      window.localStorage.removeItem(STEPUP_EXP_KEY);
      return null;
    }
    return window.localStorage.getItem(STEPUP_KEY);
  } catch (_) { return null; }
}

export function storeStepupToken(token, expiresInSeconds) {
  try {
    window.localStorage.setItem(STEPUP_KEY, token);
    // Refresh 30s before the server thinks it expires so we don't get a
    // false-negative on the boundary.
    const exp = Date.now() + (expiresInSeconds - 30) * 1000;
    window.localStorage.setItem(STEPUP_EXP_KEY, String(exp));
  } catch (_) { /* noop */ }
}

export function clearStepupToken() {
  try {
    window.localStorage.removeItem(STEPUP_KEY);
    window.localStorage.removeItem(STEPUP_EXP_KEY);
  } catch (_) { /* noop */ }
}

// Open the challenge modal. Returns a Promise that resolves to a
// stepup token on success, or rejects on cancel/error. Concurrent calls
// (e.g. 18 parallel admin requests each getting 401) all share the
// SAME in-flight promise — so the user sees one dialog and one
// resolution kicks all 18 retries.
let _inflightChallenge = null;

export function openTwofaChallenge() {
  if (_inflightChallenge) return _inflightChallenge;
  _inflightChallenge = new Promise((resolve, reject) => {
    const handler = (e) => {
      window.removeEventListener(`${CHALLENGE_EVENT}:result`, handler);
      _inflightChallenge = null;
      const { ok, token, expiresIn, error } = e.detail || {};
      if (ok && token) {
        storeStepupToken(token, expiresIn || 300);
        resolve(token);
      } else {
        reject(new Error(error || "2FA challenge cancelled"));
      }
    };
    window.addEventListener(`${CHALLENGE_EVENT}:result`, handler);
    window.dispatchEvent(new CustomEvent(CHALLENGE_EVENT));
  });
  return _inflightChallenge;
}

// Called by the challenge dialog when the user finishes (success or cancel).
export function resolveTwofaChallenge(ok, token, expiresIn, error) {
  window.dispatchEvent(new CustomEvent(`${CHALLENGE_EVENT}:result`, {
    detail: { ok, token, expiresIn, error },
  }));
}

export { CHALLENGE_EVENT };
